import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Line3DChart } from './Line3DChart';
import { LineChart } from './LineChart';
import { orderedPositions } from '../../surveyPositions';
import { isCoreTrakSurvey } from '../../surveyClass';
import { getMetadataValue } from '../../drillholeSummary';
import { withCollarOrigin } from '../../coreOrientation';

// The keywords are matched against the customer's own metadata column names, which
// the CSV import stores verbatim, so they stay English in every language.
const calculatePlannedTrajectory = (metadata) => {
    const depth = getMetadataValue(metadata, 'depth');
    const azimuth = getMetadataValue(metadata, 'azimuth');
    const dip = getMetadataValue(metadata, 'dip');
    if (depth == null || azimuth == null || dip == null || isNaN(depth) || isNaN(azimuth) || isNaN(dip)) return null;

    const azRad = azimuth * Math.PI / 180;
    const dipRad = dip * Math.PI / 180;
    const horizontalDistance = depth * Math.cos(dipRad);
    const tvd = -depth * Math.sin(dipRad);
    const easting = horizontalDistance * Math.sin(azRad);
    const northing = horizontalDistance * Math.cos(azRad);

    return { depth, easting, northing, tvd };
};

// Fixed categorical slots, assigned by a survey's position in the drillhole so a
// survey keeps its colour when others are hidden, and keeps it across the plan,
// section and 3D views. Never cycled into generated hues: past the eighth survey
// the hue repeats and a dashed line carries the difference instead.
const SERIES_PALETTE = [
    '#2a78d6', '#eb6834', '#1baf7a', '#eda100',
    '#e87ba4', '#008300', '#4a3aa7', '#e34948'
];

const seriesColour = (index) => SERIES_PALETTE[index % SERIES_PALETTE.length];
const seriesDash = (index) => (index >= SERIES_PALETTE.length ? { type: 'dashed' } : undefined);

// Not a survey file id, so it can never collide with one in the hidden set.
const COMPOSITE_ID = 'coretrak-composite';

// CoreTrak takes one shot per run, so each run on its own draws a straight line
// from the collar and several runs fan out of it. With two or more runs the
// server joins every run's shot into one trajectory, added as a series of its
// own; the per-run lines start hidden but stay in the list so a single run can
// still be inspected. If the composite cannot be fetched the runs are shown as
// before.
const withCoreTrakComposite = (surveys, colourSlot) => {
    const runs = surveys.filter(survey => survey.isCoreTrak);
    if (runs.length < 2) return Promise.resolve({ surveys, hidden: new Set() });
    return fetch('api/surveys/composite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileIds: runs.map(run => run.fileId) })
    })
        .then(response => (response.ok ? response.json() : Promise.reject(response)))
        .then(composite => {
            // Tied back to the collar at the origin, as on the Core Orientation
            // tab, rather than starting in mid-air at the first run's shot.
            const { lines, prepended } = withCollarOrigin(orderedPositions(composite));
            return {
                surveys: [...surveys, {
                    fileId: COMPOSITE_ID,
                    index: colourSlot,
                    // Named at render time, so the label follows a language change.
                    isComposite: true,
                    surveyLines: lines,
                    // The collar is not a measurement; repeatability skips it.
                    collarOffset: prepended ? 1 : 0
                }],
                hidden: new Set(runs.map(run => run.fileId))
            };
        })
        .catch(() => ({ surveys, hidden: new Set() }));
};

export const ChartsTab = ({ drillhole }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [loadedSurveys, setLoadedSurveys] = useState([]);
    // Hidden rather than selected, so a drillhole opens with everything drawn and
    // a survey that arrives later defaults to visible.
    const [hiddenFileIds, setHiddenFileIds] = useState(() => new Set());

    const planned = useMemo(() => calculatePlannedTrajectory(drillhole.metadata), [drillhole.metadata]);

    const surveyCollection = useMemo(
        () => loadedSurveys.map(survey => (survey.isComposite
            ? { ...survey, name: t('charts.coreTrakComposite') }
            : survey)),
        [loadedSurveys, t]);

    useEffect(() => {
        let cancelled = false;
        setLoadedSurveys([]);
        setHiddenFileIds(new Set());
        setLoading(true);

        // Loaded as one batch rather than appended as each response lands: the
        // order surveys arrive in is not the order they are listed in, and the
        // colour a survey gets is its index here. Appending also let a response
        // for the previous drillhole land in the new one's collection.
        Promise.all((drillhole.surveys || []).map((surveyReference, index) =>
            fetch('api/surveys/' + surveyReference.fileId, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            })
                .then(response => response.json())
                // Positions are computed server-side into each shot's PositionSet;
                // orderedPositions filters to approved shots and orders them
                // (chronological round-trip for continuous FastTrak, depth-ascending
                // otherwise) so the line traces the tool's actual path. No synthetic
                // collar is present, so collarOffset is 0.
                .then(data => (data.Created ? {
                    fileId: surveyReference.fileId,
                    index,
                    name: data.Name,
                    surveyLines: orderedPositions(data),
                    collarOffset: 0,
                    isCoreTrak: isCoreTrakSurvey(data)
                } : null))
                .catch(() => null)
        ))
            // The composite takes the next colour slot after the drillhole's own surveys.
            .then(results => withCoreTrakComposite(results.filter(Boolean), (drillhole.surveys || []).length))
            .then(({ surveys, hidden }) => {
                if (cancelled) return;
                setLoadedSurveys(surveys);
                setHiddenFileIds(hidden);
                setLoading(false);
            });

        return () => { cancelled = true; };
    }, [drillhole])

    // Everything below — charts, repeatability and target proximity — reads the
    // visible subset, so hiding a survey removes it from the numbers as well as
    // the lines.
    const visibleSurveys = useMemo(
        () => surveyCollection.filter(survey => !hiddenFileIds.has(survey.fileId)),
        [surveyCollection, hiddenFileIds]);

    // CoreTrak runs are left out of the selector when the composite stands in for
    // them: each is a single shot, so a hole with many runs would fill the panel
    // with a row per point, and the composite already carries them as one line.
    // Nothing else is left out - a regular survey is always listed, however many
    // shots it has, so no series is ever drawn without a row to hide it by. With
    // no composite (one run, or the composite could not be fetched) the runs are
    // listed like anything else. Show all / Hide all act on the listed surveys
    // alone, so Show all does not bring the hidden runs back beside the composite.
    const selectable = useMemo(() => {
        const hasComposite = surveyCollection.some(survey => survey.isComposite);
        return surveyCollection.filter(survey => !(hasComposite && survey.isCoreTrak));
    }, [surveyCollection]);
    const selectableHidden = selectable.some(survey => hiddenFileIds.has(survey.fileId));
    const selectableShown = selectable.some(survey => !hiddenFileIds.has(survey.fileId));
    const showAllSelectable = () => setHiddenFileIds(hidden =>
        new Set([...hidden].filter(id => !selectable.some(survey => survey.fileId === id))));
    const hideAllSelectable = () => setHiddenFileIds(hidden =>
        new Set([...hidden, ...selectable.map(survey => survey.fileId)]));

    const toggleSurvey = (fileId) => setHiddenFileIds(hidden => {
        const next = new Set(hidden);
        if (next.has(fileId)) next.delete(fileId); else next.add(fileId);
        return next;
    });

    const showOnly = (fileId) =>
        setHiddenFileIds(new Set(surveyCollection.map(s => s.fileId).filter(id => id !== fileId)));

    const plannedName = t('charts.planned');

    const plannedPlanSeries = planned ? {
        name: plannedName,
        points: [[0, 0], [planned.easting, planned.northing]],
        lineStyle: { type: 'dashed', width: 2 }
    } : null;

    const plannedSectionSeries = planned ? {
        name: plannedName,
        points: [[0, 0], [Math.sqrt(planned.easting ** 2 + planned.northing ** 2), -planned.tvd]],
        lineStyle: { type: 'dashed', width: 2 }
    } : null;

    const planSeries = visibleSurveys.map((survey) => ({
        name: survey.name,
        color: seriesColour(survey.index),
        lineStyle: seriesDash(survey.index),
        points: survey.surveyLines?.map((line) => [line.easting, line.northing])
    }));
    if (plannedPlanSeries) planSeries.push(plannedPlanSeries);

    const sectionSeries = visibleSurveys.map((survey) => ({
        name: survey.name,
        color: seriesColour(survey.index),
        lineStyle: seriesDash(survey.index),
        points: survey.surveyLines?.map((line) => [line.stepout, -line.tvd])
    }));
    if (plannedSectionSeries) sectionSeries.push(plannedSectionSeries);

    const threeDSeries = visibleSurveys.map((survey) => ({
        name: survey.name,
        color: seriesColour(survey.index),
        lineStyle: seriesDash(survey.index),
        points: survey.surveyLines?.map((line) => [line.easting, line.northing, -line.tvd])
    }));
    if (planned) {
        threeDSeries.push({
            name: plannedName,
            points: [[0, 0, 0], [planned.easting, planned.northing, -planned.tvd]],
            lineStyle: { type: 'dashed', width: 2 }
        });
    }

    // For continuous round-trip surveys (FastTrak), measure how closely the
    // outrun retraces the inrun. The shallowest pair of inrun/outrun shots
    // within DEPTH_MATCH_TOLERANCE_M of each other is the most stringent test
    // (longest path, most cumulative drift). Returns radial displacement
    // between the two passes at that pair, plus that displacement as a
    // fraction of the path length walked between them.
    const repeatabilities = useMemo(() => {
        const DEPTH_MATCH_TOLERANCE_M = 1.0;
        const out = [];
        for (const survey of visibleSurveys) {
            const lines = survey.surveyLines;
            if (!lines || lines.length < 4) continue;
            let peakIdx = 0;
            for (let i = 1; i < lines.length; i++) {
                if (lines[i].depth > lines[peakIdx].depth) peakIdx = i;
            }
            if (peakIdx === 0 || peakIdx === lines.length - 1) continue;
            // Skip the prepended collar (an extrapolated depth=0 anchor, not a
            // real measurement) so it can't pretend to match an outrun shot.
            const collarOffset = survey.collarOffset || 0;
            // Walk inrun shots from shallowest to deepest. For each, find the
            // closest outrun shot by depth; if within tolerance, take this
            // pair (shallowest valid match). This tolerates depth-encoder
            // noise where the same physical station reads slightly different
            // depths on the way down vs the way up.
            const inrunOrdered = [];
            for (let i = collarOffset; i <= peakIdx; i++) inrunOrdered.push(i);
            inrunOrdered.sort((i, j) => lines[i].depth - lines[j].depth);
            let pair = null;
            for (const inrunIdx of inrunOrdered) {
                const inDepth = lines[inrunIdx].depth;
                let bestOutIdx = -1, bestDelta = Infinity;
                for (let j = peakIdx; j < lines.length; j++) {
                    const delta = Math.abs(lines[j].depth - inDepth);
                    if (delta < bestDelta) { bestDelta = delta; bestOutIdx = j; }
                }
                if (bestOutIdx >= 0 && bestDelta <= DEPTH_MATCH_TOLERANCE_M) {
                    pair = {
                        inrunIdx,
                        outrunIdx: bestOutIdx,
                        inrunDepth: inDepth,
                        outrunDepth: lines[bestOutIdx].depth,
                        depthDelta: bestDelta
                    };
                    break;
                }
            }
            if (!pair) continue;
            const a = lines[pair.inrunIdx];
            const b = lines[pair.outrunIdx];
            const dx = a.northing - b.northing;
            const dy = a.easting - b.easting;
            const dz = a.tvd - b.tvd;
            const radial = Math.sqrt(dx * dx + dy * dy + dz * dz);
            // Path length is the total measured-depth travelled between the
            // two passes — walks down from inrun shot to peak then back up
            // to the outrun shot.
            let pathLength = 0;
            const lo = Math.min(pair.inrunIdx, pair.outrunIdx);
            const hi = Math.max(pair.inrunIdx, pair.outrunIdx);
            for (let i = lo; i < hi; i++) {
                pathLength += Math.abs(lines[i + 1].depth - lines[i].depth);
            }
            const mismatchPct = pathLength > 0 ? (radial / pathLength) * 100 : 0;
            out.push({
                name: survey.name,
                inrunDepth: pair.inrunDepth,
                outrunDepth: pair.outrunDepth,
                depthDelta: pair.depthDelta,
                radial,
                pathLength,
                mismatchPct
            });
        }
        return out;
    }, [visibleSurveys]);

    // Compute target proximity summary: pick the survey whose bottom-of-hole
    // is closest (3D) to the planned target endpoint.
    const summary = useMemo(() => {
        if (!planned || visibleSurveys.length === 0) return null;

        let best = null;
        for (const survey of visibleSurveys) {
            const lines = survey.surveyLines;
            if (!lines || lines.length === 0) continue;
            // BOH = deepest position. For depth-sorted multishot this is the
            // last entry; for chronologically-ordered continuous surveys the
            // deepest is somewhere in the middle (the turn-around point).
            let boh = lines[0];
            for (let i = 1; i < lines.length; i++) {
                if (lines[i].depth > boh.depth) boh = lines[i];
            }
            const dx = boh.easting - planned.easting;
            const dy = boh.northing - planned.northing;
            const dz = boh.tvd - planned.tvd;
            const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
            if (!best || dist < best.displacement) {
                // Radial displacement: perpendicular distance from BOH to the
                // planned trajectory line (origin → planned endpoint).
                // Formula: |BOH × D| / |D|  (cross product magnitude / direction magnitude)
                const cx = boh.northing * planned.tvd - boh.tvd * planned.northing;
                const cy = boh.tvd * planned.easting - boh.easting * planned.tvd;
                const cz = boh.easting * planned.northing - boh.northing * planned.easting;
                const dLen = Math.sqrt(planned.easting ** 2 + planned.northing ** 2 + planned.tvd ** 2);
                const radial = dLen > 0 ? Math.sqrt(cx * cx + cy * cy + cz * cz) / dLen : 0;
                best = { name: survey.name, boh, displacement: dist, radial, actualDepth: boh.depth };
            }
        }
        return best;
    }, [planned, visibleSurveys]);

    return (
        <div className="container" style={{ marginTop: 10 }}>
            {summary &&
                <div className="row mb-3">
                    <div className="col-lg-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">{t('charts.targetProximity')}</h5>
                                <p className="card-text mb-1">
                                    <strong>{t('charts.holeDepth')}</strong> {t('charts.holeDepthValue', { actual: summary.actualDepth.toFixed(1), planned: planned.depth.toFixed(1) })}
                                </p>
                                <p className="card-text mb-1">
                                    <strong>{t('charts.displacementFromTarget')}</strong> {summary.displacement.toFixed(2)}m
                                </p>
                                <p className="card-text mb-0">
                                    <strong>{t('charts.radialFromTrajectory')}</strong> {summary.radial.toFixed(2)}m
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {repeatabilities.length > 0 &&
                <div className="row mb-3">
                    <div className="col-lg-12">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">{t('charts.repeatability')}</h5>
                                {repeatabilities.map((r, i) => (
                                    <p className="card-text mb-1" key={i}>
                                        <strong>{r.name}:</strong> {t('charts.radialAt', { radial: r.radial.toFixed(3) })}{' '}
                                        &nbsp;{r.depthDelta < 0.005
                                            ? t('charts.atDepth', { depth: r.inrunDepth.toFixed(2) })
                                            : t('charts.inrunOutrun', { inrun: r.inrunDepth.toFixed(2), outrun: r.outrunDepth.toFixed(2) })}
                                        &nbsp;{t('charts.overPath', { percent: r.mismatchPct.toFixed(2), path: r.pathLength.toFixed(2) })}
                                    </p>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            }
            <div className="row">
                <div className="col-lg-12">
                    <h3>{t('charts.title')}</h3>
                </div>
            </div>
            {loading && surveyCollection.length === 0 &&
                <div className="row mb-3">
                    <div className="col-lg-12"><em>{t('charts.loadingSurveys')}</em></div>
                </div>
            }
            {selectable.length > 0 &&
                <div className="row mb-3">
                    <div className="col-lg-12">
                        {/* A plain bordered panel rather than a .card: cards are
                            themed solid red app-wide (custom.css), which the colour
                            swatches and link buttons cannot be read against. The
                            palette is chosen for a white surface. */}
                        <div className="border rounded bg-white p-3">
                            <div>
                                <div className="d-flex align-items-center flex-wrap mb-2">
                                    <h5 className="mb-0">{t('charts.surveysShown')}</h5>
                                    <button type="button" className="btn btn-sm btn-link ms-3"
                                        disabled={!selectableHidden}
                                        onClick={showAllSelectable}>{t('charts.showAll')}</button>
                                    <button type="button" className="btn btn-sm btn-link"
                                        disabled={!selectableShown}
                                        onClick={hideAllSelectable}>
                                        {t('charts.hideAll')}
                                    </button>
                                </div>
                                <div className="d-flex flex-wrap">
                                    {selectable.map(survey => {
                                        const shown = !hiddenFileIds.has(survey.fileId);
                                        return (
                                            <div className="form-check d-flex align-items-center me-4 mb-1"
                                                key={survey.fileId}>
                                                <input className="form-check-input" type="checkbox"
                                                    id={`chart-survey-${survey.fileId}`}
                                                    checked={shown}
                                                    onChange={() => toggleSurvey(survey.fileId)} />
                                                <label className="form-check-label d-flex align-items-center"
                                                    htmlFor={`chart-survey-${survey.fileId}`}>
                                                    {/* The swatch matches this survey's line in all three
                                                        charts. The name is always present beside it, so
                                                        identity never rests on colour alone. */}
                                                    <span aria-hidden="true" style={{
                                                        display: 'inline-block',
                                                        width: 12, height: 12, borderRadius: 2,
                                                        marginRight: 6,
                                                        backgroundColor: seriesColour(survey.index),
                                                        opacity: shown ? 1 : 0.3
                                                    }} />
                                                    <span className={shown ? '' : 'text-muted'}>{survey.name}</span>
                                                </label>
                                                <button type="button" className="btn btn-sm btn-link py-0 ms-2"
                                                    title={t('charts.showOnlyThis')}
                                                    onClick={() => showOnly(survey.fileId)}>{t('charts.only')}</button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }
            <div className="row">
                {(visibleSurveys.length > 0 || planned) &&
                    <div className="col-lg-6">
                        <LineChart title={t('charts.plan')} xAxisName={t('charts.east')} yAxisName={t('charts.north')}
                            seriesCollection={planSeries} />
                    </div>
                }
                {(visibleSurveys.length > 0 || planned) &&
                    <div className="col-lg-6">
                        <LineChart title={t('charts.section')} xAxisName={t('charts.stepout')} yAxisName={t('charts.tvd')}
                            seriesCollection={sectionSeries} />
                    </div>
                }
                {(visibleSurveys.length > 0 || planned) &&
                    <div className="col-lg-12">
                        <Line3DChart title={t('charts.threeD')} xAxisName={t('charts.east')} yAxisName={t('charts.north')} zAxisName={t('charts.tvd')}
                            seriesCollection={threeDSeries} />
                    </div>
                }
            </div>
        </div>
    );
}
